import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import {
  dismissFlag,
  showFlag,
  type FlagId,
} from '@trello/nachos/experimental-flags';

import { WithEnterpriseManagedOverride } from 'app/src/components/UpgradePrompts';
import { ADSUpgradePromptButton } from 'app/src/components/UpgradePrompts/ADSUpgradePromptButton';
import { useUpsellData } from './useUpsellData';
import { useWorkspaceAri } from './useWorkspaceAri';

export const useUpsell = () => {
  const { upsellCohort, isLoading: isUpsellCohortLoading } = useUpsellData();
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [flagId, setFlagId] = useState<FlagId | undefined>(undefined);
  const { workspaceId } = useWorkspaceAri();

  const handleActionClick = useCallback(
    (id: FlagId) => {
      Analytics.sendClickedButtonEvent({
        buttonName: id,
        attributes: {
          isFreeTrial: upsellCohort === 'trialAvailable',
        },
        source: 'plannerScreen',
        containers: formatContainers({ workspaceId }),
      });
      setShowUpsellModal(true);
      setFlagId(id);
      dismissFlag({ id });
    },
    [upsellCohort, workspaceId],
  );

  const showPaygatingFlag = () => {
    if (upsellCohort === 'paidUser') {
      return;
    }

    if (upsellCohort === 'upgradeAvailableWithoutUpsell') {
      showFlag({
        id: 'plannerPaidActionDesktop',
        title: (
          <FormattedMessage
            id="templates.planner.upsell-flag-desktop-title"
            defaultMessage="Scheduling isn’t available"
            description="Message to user explaining that the action they just performed is not available to their account"
          />
        ),
        description: (
          <FormattedMessage
            id="templates.planner.upsell-flag-desktop-description"
            defaultMessage="Scheduling focus time and to-dos isn’t available for your account."
            description="Message to user explaining that the action they just performed is not available to their account"
          />
        ),
        appearance: 'error',
      });
      Analytics.sendUIEvent({
        action: 'viewed',
        actionSubject: 'flag',
        actionSubjectId: 'plannerPaidActionDesktop',
        source: 'plannerScreen',
        attributes: {
          isFreeTrial: false,
        },
        containers: formatContainers({ workspaceId }),
      });
      return;
    }

    showFlag({
      id: 'plannerPaidAction',
      title: (
        <FormattedMessage
          id="templates.planner.upsell-flag-title"
          defaultMessage="Upgrade to get full Planner access"
          description="Title on flag shown prompting user to upgrade their account to get full access to Planner"
        />
      ),
      description: (
        <FormattedMessage
          id="templates.planner.upsell-flag-description"
          defaultMessage="Standard and Premium plans support scheduling to-dos, creating focus time, and linking cards to events."
          description="Description of functionality available with Planner on paid plans"
        />
      ),
      appearance: 'info',
      actions: [
        {
          content: (
            <ADSUpgradePromptButton
              onClick={() => handleActionClick('plannerPaidAction')}
            >
              <WithEnterpriseManagedOverride>
                {upsellCohort === 'trialAvailable' ? (
                  <FormattedMessage
                    id="templates.planner.try-premium-free"
                    defaultMessage="Try Premium free"
                    description="Button call-to-action to try Premium for free"
                  />
                ) : (
                  <FormattedMessage
                    id="templates.planner.view-plans"
                    defaultMessage="View plans"
                    description="Button call-to-action to view Trello's paid plans"
                  />
                )}
              </WithEnterpriseManagedOverride>
            </ADSUpgradePromptButton>
          ),
          type: 'link',
        },
      ],
    });
    Analytics.sendUIEvent({
      action: 'viewed',
      actionSubject: 'flag',
      actionSubjectId: 'plannerPaidAction',
      source: 'plannerScreen',
      attributes: {
        isFreeTrial: upsellCohort === 'trialAvailable',
      },
      containers: formatContainers({ workspaceId }),
    });
  };

  const showAuthPaygatingFlag = () => {
    if (upsellCohort === 'paidUser') {
      return;
    }

    if (upsellCohort === 'upgradeAvailableWithoutUpsell') {
      showFlag({
        id: 'plannerConnectedDesktop',
        title: (
          <FormattedMessage
            id="templates.planner.upsell-flag-connected-title"
            defaultMessage="Your calendar is connected to Planner"
            description="Title on flag after calendar is connected to planner"
          />
        ),
        appearance: 'success',
      });
      Analytics.sendUIEvent({
        action: 'viewed',
        actionSubject: 'flag',
        actionSubjectId: 'plannerConnectedDesktop',
        source: 'plannerScreen',
        attributes: {
          isFreeTrial: false,
        },
        containers: formatContainers({ workspaceId }),
      });
      return;
    }

    showFlag({
      id: 'plannerConnected',
      title: (
        <FormattedMessage
          id="templates.planner.upsell-flag-connected-title"
          defaultMessage="Your calendar is connected to Planner"
          description="Title on flag after calendar is connected to planner"
        />
      ),
      description: (
        <FormattedMessage
          id="templates.planner.upsell-flag-connected-description"
          defaultMessage="Upgrade to Standard or Premium to schedule focus time and to-dos."
          description="Prompt to upgrade paid plans to schedule on planner"
        />
      ),
      appearance: 'success',
      actions: [
        {
          content: (
            <ADSUpgradePromptButton
              onClick={() => handleActionClick('plannerConnected')}
            >
              <WithEnterpriseManagedOverride>
                {upsellCohort === 'trialAvailable' ? (
                  <FormattedMessage
                    id="templates.planner.try-premium-free"
                    defaultMessage="Try Premium free"
                    description="Button call-to-action to try Premium for free"
                  />
                ) : (
                  <FormattedMessage
                    id="templates.planner.view-plans"
                    defaultMessage="View plans"
                    description="Button call-to-action to view Trello's paid plans"
                  />
                )}
              </WithEnterpriseManagedOverride>
            </ADSUpgradePromptButton>
          ),
          type: 'link',
        },
      ],
    });
    Analytics.sendUIEvent({
      action: 'viewed',
      actionSubject: 'flag',
      actionSubjectId: 'plannerConnected',
      source: 'plannerScreen',
      attributes: {
        isFreeTrial: upsellCohort === 'trialAvailable',
      },
      containers: formatContainers({ workspaceId }),
    });
  };

  return {
    upsellCohort,
    isUpsellCohortLoading,
    showUpsellModal,
    setShowUpsellModal,
    flagId,
    showPaygatingFlag,
    showAuthPaygatingFlag,
  };
};
